import { useCallback, useState } from "react"

import { parseAndThrowAppError } from "../../../../errors/errorProcessors"
import { postClientProtected } from "../../clients/clients"
import { IChangeDetail } from "../../types/dnoThreshold/dnoThresholdTypes"
import {
    DNO_THRESHOLD_CHANGE_REQUESTS,
    DNO_THRESHOLD_REVIEW_CHANGE_REQUEST,
} from "../../urls/private"

interface ISubmitChangeRequest {
    submitChange: (
        title: string,
        comment: string,
        changes: IChangeDetail[]
    ) => Promise<boolean>
    isSubmitting: boolean
    error: string | null
}

/**
 * Hook to submit a new threshold change request
 * @returns Submit function, loading state, and error state
 */
const useSubmitChangeRequest = (): ISubmitChangeRequest => {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const submitChange = useCallback(
        async (
            title: string,
            comment: string,
            changes: IChangeDetail[]
        ): Promise<boolean> => {
            try {
                setError(null)
                setIsSubmitting(true)
                await postClientProtected(DNO_THRESHOLD_CHANGE_REQUESTS, {
                    title,
                    comment,
                    changes,
                })
                return true
            } catch (err) {
                const appError = parseAndThrowAppError(err)
                setError(appError.displayDetail)
                return false
            } finally {
                setIsSubmitting(false)
            }
        },
        []
    )

    return { submitChange, isSubmitting, error }
}

interface IApproveRejectChangeRequest {
    approve: (changeRequestId: string, comment: string) => Promise<boolean>
    reject: (changeRequestId: string, comment: string) => Promise<boolean>
    isProcessing: boolean
    error: string | null
}

/**
 * Hook to approve or reject a threshold change request
 * @returns Approve/reject functions, loading state, and error state
 */
const useApproveRejectChangeRequest = (): IApproveRejectChangeRequest => {
    const [isProcessing, setIsProcessing] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const approve = useCallback(
        async (changeRequestId: string, comment: string): Promise<boolean> => {
            try {
                setError(null)
                setIsProcessing(true)
                await postClientProtected(DNO_THRESHOLD_REVIEW_CHANGE_REQUEST, {
                    changeRequestId,
                    decision: "approve",
                    comment,
                })
                return true
            } catch (err) {
                const appError = parseAndThrowAppError(err)
                setError(appError.displayDetail)
                return false
            } finally {
                setIsProcessing(false)
            }
        },
        []
    )

    const reject = useCallback(
        async (changeRequestId: string, comment: string): Promise<boolean> => {
            try {
                setError(null)
                setIsProcessing(true)
                await postClientProtected(DNO_THRESHOLD_REVIEW_CHANGE_REQUEST, {
                    changeRequestId,
                    decision: "reject",
                    comment,
                })
                return true
            } catch (err) {
                const appError = parseAndThrowAppError(err)
                setError(appError.displayDetail)
                return false
            } finally {
                setIsProcessing(false)
            }
        },
        []
    )

    return { approve, reject, isProcessing, error }
}

export { useSubmitChangeRequest, useApproveRejectChangeRequest }
